import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Copy, Plus } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/Dialog";
import { useCreateApiKeyMutation } from "@/features/apikeys/hooks/useApiKeys";

export function CreateApiKeyDialog() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [rawKey, setRawKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const create = useCreateApiKeyMutation();

  async function handleCreate() {
    const result = await create.mutateAsync(name);
    setRawKey(result.rawKey);
  }

  async function handleCopy() {
    if (!rawKey) return;
    await navigator.clipboard.writeText(rawKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) {
      setName("");
      setRawKey(null);
      setCopied(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger
        render={
          <Button>
            <Plus /> {t("apiKeys.newKey")}
          </Button>
        }
      />
      <DialogContent>
        {rawKey ? (
          <>
            <DialogHeader>
              <DialogTitle>{t("apiKeys.dialog.createdTitle")}</DialogTitle>
              <DialogDescription>{t("apiKeys.dialog.createdDescription")}</DialogDescription>
            </DialogHeader>
            <div className="flex items-center gap-2 rounded-lg border border-border bg-muted px-3 py-2">
              <code className="flex-1 truncate font-mono text-xs">{rawKey}</code>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={handleCopy}
                aria-label={t("apiKeys.dialog.copyAria")}
                title={t("apiKeys.dialog.copyAria")}
              >
                {copied ? <Check /> : <Copy />}
              </Button>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{t("apiKeys.dialog.createTitle")}</DialogTitle>
              <DialogDescription>{t("apiKeys.dialog.createDescription")}</DialogDescription>
            </DialogHeader>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("apiKeys.dialog.namePlaceholder")}
              maxLength={100}
              autoFocus
            />
            <Button onClick={handleCreate} disabled={create.isPending}>
              {create.isPending ? t("apiKeys.dialog.generating") : t("apiKeys.dialog.generate")}
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
